// ─── src/data/uae/regions.js ─────────────────────────────────────────────────
// UAE region entities. Dubai is primary; Abu Dhabi is secondary.
// Ras Al Khaimah added as tertiary, growing luxury market.

export const UAE_REGIONS = [
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMARY REGIONS
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: "dubai",
    countrySlug: "uae",
    slug: "dubai",
    name: "Dubai",
    priorityLevel: "primary",
    urlEnabledManual: null,
    urlEverActivated: true,
    listingCount: 0,
    canonicalRoute: "/uae/dubai",
    heroSubtitle: "Palace ballrooms, desert ceremonies, and Burj-view terraces. The world's most ambitious wedding city.",
    description: "Dubai operates at a scale no other city matches. A 500-guest palace ballroom with a full production team, lighting rig, live orchestra, custom floral installation, is not exceptional here; it is standard. The city has built an entire industry around the wedding day as spectacle.\n\nBut spectacle does not preclude intimacy. An evening ceremony in the desert, an hour from the city, with the dunes going red at sunset and a tented reception lit entirely by candles, this is also Dubai. Private island resorts on Palm Jumeirah offer their own world, cut off from the city by water and distance, where the only skyline visible is the one you want.\n\nThe production infrastructure is the real differentiator. Dubai has florists who flew in flowers from three continents for last Saturday's wedding. It has caterers managing a twelve-course tasting menu for three hundred people. It has AV teams who have rigged Burj Khalifa for New Year's Eve. All of this is available for a private wedding. The question is never whether it is possible. The question is which version of Dubai you want.",
    seoText: "Dubai is the UAE's premier luxury wedding destination, a city that combines palace-scale grandeur with intimate desert experiences and world-class production.",
    focusKeywords: ["dubai wedding venues", "luxury wedding dubai", "palace wedding dubai", "desert wedding dubai"],
    aiSummary: "Dubai, ultra-luxury palace weddings, desert celebrations, Palm Jumeirah island resorts, and world-class production. Peak November to April.",
    intentSignals: {
      high: ["dubai wedding venue hire", "luxury wedding dubai planner", "palm jumeirah wedding"],
      mid: ["best wedding venues dubai", "dubai desert wedding experience", "burj khalifa wedding view"],
      low: ["dubai wedding inspiration", "getting married in dubai", "dubai wedding traditions"],
    },
    entityAliases: ["دبي", "Dubayy"],
    isLegacy: false,
    ai: {
      positioning: "The world's most ambitious wedding city, where spectacle is the baseline and production has no ceiling.",
      opening_line: "Dubai doesn't do restraint. A 500-guest palace ballroom with a live orchestra, a custom floral installation, and a lighting rig that rivals a stadium concert, that's Tuesday.",
      differentiation: "Dubai versus Abu Dhabi: Dubai is production and spectacle; Abu Dhabi is culture and refinement. Dubai asks what you want it to look like. Abu Dhabi asks what it should feel like. Couples who want their guests to be genuinely amazed choose Dubai. Couples who want their guests to feel something choose Abu Dhabi.",
      register: "ultra-luxury",
      style_affinity: ["ultra-modern", "destination-glamour", "desert-dramatic", "beach-luxury", "urban-prestige"],
      season_peak: ["november", "december", "january", "february", "march", "april"],
      season_avoid: ["june", "july", "august", "september"],
      scale_fit: "all",
      not_for: ["rustic-farmhouse", "vineyard", "wild-remote", "understated-elegant"],
      price_index: "ultra",
      cross_sell: ["abu-dhabi", "marrakech", "maldives"],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SECONDARY REGIONS
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: "abu-dhabi",
    countrySlug: "uae",
    slug: "abu-dhabi",
    name: "Abu Dhabi",
    priorityLevel: "secondary",
    urlEnabledManual: null,
    urlEverActivated: true,
    listingCount: 0,
    canonicalRoute: "/uae/abu-dhabi",
    heroSubtitle: "Cultural grandeur and island celebrations in the UAE capital.",
    description: "Abu Dhabi offers something Dubai does not attempt: restraint. The UAE capital has built its luxury wedding market around cultural depth rather than spectacle, palace venues of genuine architectural significance, Saadiyat Island resorts with direct access to one of the finest art collections in the world, and a culinary scene that has matured into genuine sophistication.\n\nSaadiyat Island is the natural centre for Abu Dhabi's luxury wedding market. Its beach resorts, set against a coastline that lacks the infrastructure density of Dubai's waterfront, offer a privacy that is increasingly rare in the Gulf. The Sheikh Zayed Grand Mosque, one of the world's most architecturally significant buildings, is fifteen minutes away. The juxtaposition of cultural grandeur and island intimacy is unique to Abu Dhabi.\n\nFor couples who want the UAE's infrastructure and production quality but prefer a wedding that feels like a considered choice rather than a maximum-effort display, Abu Dhabi is the correct answer.",
    seoText: "Abu Dhabi combines palace-scale cultural venues with Saadiyat Island resort privacy for a luxury UAE wedding that values refinement over spectacle.",
    focusKeywords: ["abu dhabi wedding venues", "luxury wedding abu dhabi", "saadiyat island wedding"],
    aiSummary: "Abu Dhabi, cultural palace venues, Saadiyat Island resorts, and refined alternatives to Dubai's spectacle. Peak October to April.",
    intentSignals: {
      high: ["abu dhabi wedding venue hire", "saadiyat island wedding"],
      mid: ["best wedding venues abu dhabi", "abu dhabi vs dubai wedding"],
      low: ["abu dhabi wedding inspiration", "getting married abu dhabi"],
    },
    entityAliases: ["أبوظبي", "Abu Zaby"],
    isLegacy: false,
    ai: {
      positioning: "The UAE's cultural capital, where architectural grandeur meets island privacy and the production quality is identical to Dubai's.",
      opening_line: "Abu Dhabi is what you choose when you want Dubai's infrastructure without Dubai's need to impress. The venues are serious, the food has matured, and Saadiyat Island keeps the rest of the city at arm's length.",
      differentiation: "Abu Dhabi versus Dubai: the production ceiling is the same. What differs is the register. Dubai curates for impact. Abu Dhabi curates for meaning. If the Sheikh Zayed Grand Mosque matters more to you than the Burj Khalifa view, you already know which one to choose.",
      register: "cultural-grand",
      style_affinity: ["cultural-ceremony", "beach-luxury", "urban-prestige", "understated-elegant"],
      season_peak: ["october", "november", "december", "january", "february", "march", "april"],
      season_avoid: ["june", "july", "august", "september"],
      scale_fit: "all",
      not_for: ["rustic-farmhouse", "vineyard", "wild-remote"],
      price_index: "ultra",
      cross_sell: ["dubai", "marrakech"],
    },
  },

  {
    id: "ras-al-khaimah",
    countrySlug: "uae",
    slug: "ras-al-khaimah",
    name: "Ras Al Khaimah",
    priorityLevel: "secondary",
    urlEnabledManual: null,
    urlEverActivated: false,
    listingCount: 0,
    canonicalRoute: "/uae/ras-al-khaimah",
    heroSubtitle: "Hajar Mountain retreats and private island resorts, a quieter corner of the Gulf.",
    description: "Ras Al Khaimah sits at the northern tip of the UAE, where the Hajar Mountains meet the Arabian Gulf. Its luxury wedding market is the newest in the Emirates and, for that reason, the least crowded. The Al Hamra and Waldorf Astoria properties have established a high-end baseline, and the Jais mountain terrain, home to the world's longest zipline, offers an outdoor ceremony setting found nowhere else in the Gulf region.\n\nFor couples who want the UAE's climate window and five-star infrastructure but prefer a landscape that involves something other than urban skyline or engineered coastline, Ras Al Khaimah is the answer.",
    seoText: "Ras Al Khaimah combines mountain landscapes with luxury resort wedding venues for a UAE experience distinct from Dubai and Abu Dhabi.",
    focusKeywords: ["ras al khaimah wedding", "rak wedding venue", "jais mountain wedding uae"],
    aiSummary: "Ras Al Khaimah, mountain terrain, quieter Gulf coast, and growing luxury resort market. An alternative to Dubai for couples wanting landscape over skyline.",
    intentSignals: {
      high: ["ras al khaimah wedding venue"],
      mid: ["rak wedding ideas", "jais mountain wedding"],
      low: ["ras al khaimah wedding inspiration"],
    },
    entityAliases: ["RAK", "رأس الخيمة"],
    isLegacy: false,
    ai: {
      positioning: "The UAE's quietest corner, Hajar Mountain terrain and Gulf coast privacy without Dubai's noise.",
      opening_line: "Ras Al Khaimah is what happens when you want the UAE's climate and infrastructure but the ceremony needs to involve actual landscape, mountains, desert, coastline that hasn't been engineered.",
      differentiation: "RAK versus Dubai: Dubai is the production. RAK is the setting. Couples who want a dramatic outdoor ceremony with a five-star reception behind it, and don't need a skyline, find what they're looking for here.",
      register: "wild-remote",
      style_affinity: ["desert-dramatic", "intimate", "rustic-luxe", "outdoor-ceremony"],
      season_peak: ["november", "december", "january", "february", "march"],
      season_avoid: ["june", "july", "august", "september"],
      scale_fit: "intimate",
      not_for: ["urban-prestige", "ultra-modern", "destination-glamour"],
      price_index: "premium",
      cross_sell: ["dubai", "abu-dhabi"],
    },
  },
];
